import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  useLocation,
  useNavigate,
  unstable_usePrompt as usePrompt,
} from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import useUnload from "../../hooks/useUnload";
import useGetLocalePath from "../../hooks/useGetLocalePath";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import useUnregisterNewsletter from "../../hooks/useUnregNewsletter";

import {
  setBlur,
  setShowNewsletterUnregisterDialog,
} from "../../redux/reducers/uiReducer";

import { DevTool } from "@hookform/devtools";
import ReCAPTCHA from "react-google-recaptcha";
import { yupResolver } from "@hookform/resolvers/yup";

import NewsletterUnregForm from "./newsletter-unreg-form";
import { errorToast, successToast, warnToast } from "../toast";
import { getNewsletterUnregSchema } from "../../schemas/newsletterSchema";

import {
  isEmpty,
  isEmptyObject,
  equalsIgnoringCase,
} from "../../utils/validators";
import { logDebug, logError, logWarn } from "../../utils/logger";

function NewsletterUnregisterDialog() {
  const { t } = useTranslation();

  const recaptchaRef = useRef(null);
  const dialogNewsletterRef = useRef(null);

  const [isBlocked, setIsBlocked] = useState(false);
  const [recaptchaLoading, setRecaptchaLoading] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { getLocalePath } = useGetLocalePath();

  const dispatch = useDispatch();
  const { settings } = useSelector((state) => state.settings);
  const { csrf_token } = settings;

  const {
    loading: loadingUnregisterNewsletter,
    data: dataUnregisterNewsletter,
    error: errorUnregisterNewsletter,
    unregisterNewsletter,
    clear: clearUnregisterNewsletter,
  } = useUnregisterNewsletter();

  const defaultValues = useMemo(() => {
    return {
      email: "",
    };
  }, []);

  const loadingForm = useMemo(() => {
    return [recaptchaLoading, loadingUnregisterNewsletter].some((i) => i);
  }, [recaptchaLoading, loadingUnregisterNewsletter]);

  const form = useForm({
    mode: "all",
    criteriaMode: "all",
    reValidateMode: "onChange",
    defaultValues: defaultValues,
    disabled: loadingUnregisterNewsletter,
    resolver: yupResolver(getNewsletterUnregSchema(t)),
  });
  const { control, formState, reset } = form;
  const { isDirty, disabled } = formState;

  useUnload({
    when: isBlocked,
    message: t("message.askConfirmation"),
  });

  usePrompt({
    when: isBlocked,
    message: t("message.askConfirmation"),
  });

  useOnClickOutside(dialogNewsletterRef, (evt) => {
    logDebug("NewsletterUnregisterDialog", "useOnClickOutside", {
      evt: evt,
    });
    if (equalsIgnoringCase(evt.target.className, "wrapper")) {
      if (isBlocked) {
        if (window.confirm(t("message.askConfirmation"))) {
          closeDialogHandler(evt);
        }
      } else {
        closeDialogHandler(evt);
      }
    }
  });

  const closeDialogHandler = useCallback(
    (evt) => {
      logDebug("NewsletterUnregisterDialog", "closeDialogHandler", {
        evt: evt,
      });
      setIsBlocked(false);
      setTimeout(() => {
        if (
          equalsIgnoringCase(
            getLocalePath(t("routes.public.newsletter_unreg")),
            location.pathname
          )
        ) {
          navigate(getLocalePath(t("routes.common.home")), {
            state: { targetId: "" },
          });
        }
      }, 100);
      setTimeout(() => {
        dispatch(setBlur(false));
        dispatch(setShowNewsletterUnregisterDialog(false));
      }, 200);
    },
    [dispatch, getLocalePath, location.pathname, navigate, t]
  );

  const onInvalidHandler = useCallback(
    (errors, evt) => {
      const handler = async (errors, evt) => {
        logError("NewsletterUnregisterDialog", "onInvalidHandler", {
          errors: errors,
          evt: evt,
        });
        errorToast(
          "NewsletterUnregisterDialog",
          errors,
          t("errors.error_form", t("errors.default"))
        );
      };
      handler(errors, evt);
    },
    [t]
  );

  const onValidHandler = useCallback(
    (data, evt) => {
      const handler = async (data, evt) => {
        if (!disabled) {
          logDebug("NewsletterUnregisterDialog", "onValidHandler", {
            data: data,
            evt: evt,
          });
          if (!isEmptyObject(data)) {
            setRecaptchaLoading(true);
            const recaptcha = await recaptchaRef.current.executeAsync();
            setRecaptchaLoading(false);
            unregisterNewsletter({
              ...data,
              csrf_token: !isEmpty(csrf_token) ? csrf_token : "",
              "g-recaptcha-response": recaptcha,
            });
          } else {
            logError("NewsletterUnregisterDialog", "onValidHandler", {
              error: "invalid_recaptcha",
              msg: t("validators.invalid_recaptcha", t("errors.default")),
            });
            errorToast(
              "NewsletterUnregisterDialog - onValidHandler",
              "invalid_recaptcha",
              t("validators.invalid_recaptcha", t("errors.default"))
            );
            recaptchaRef.current.reset();
          }
        }
      };
      handler(data, evt);
    },
    [csrf_token, disabled, t, unregisterNewsletter]
  );

  const onErroredReCaptchaHandler = useCallback(
    (evt) => {
      logError("NewsletterUnregisterDialog", "onErroredReCaptchaHandler", {
        evt: evt,
        code: "error_recaptcha",
        msg: t(`errors.error_recaptcha`, t("errors.default")),
      });
      errorToast(
        "NewsletterUnregisterDialog",
        "error_recaptcha",
        t(`errors.error_recaptcha`, t("errors.default"))
      );
      setRecaptchaLoading(false);
      recaptchaRef.current.reset();
    },
    [t]
  );

  const onExpiredReCaptchaHandler = useCallback(
    (evt) => {
      logWarn("NewsletterUnregisterDialog", "onExpiredReCaptchaHandler", {
        evt: evt,
        code: "expired_recaptcha",
        msg: t(`validators.expired_recaptcha`, t("errors.default")),
      });
      warnToast(
        "NewsletterUnregisterDialog",
        "expired_recaptcha",
        t(`validators.expired_recaptcha`, t("errors.default"))
      );
      setRecaptchaLoading(false);
      recaptchaRef.current.reset();
    },
    [t]
  );

  const asyncScriptOnLoadReCaptchaHandler = useCallback((evt) => {
    logDebug(
      "NewsletterUnregisterDialog",
      "asyncScriptOnLoadReCaptchaHandler",
      {
        evt: evt,
      }
    );
  }, []);

  useEffect(() => {
    setIsBlocked(isDirty);
  }, [isDirty]);

  useEffect(() => {
    if (!loadingUnregisterNewsletter) {
      if (errorUnregisterNewsletter) {
        logError("NewsletterUnregisterDialog", "newsletterUnregister", {
          error: errorUnregisterNewsletter,
          msg: t(`errors.${errorUnregisterNewsletter}`, t("errors.default")),
        });
        errorToast(
          "NewsletterUnregisterDialog - newsletterUnregister",
          errorUnregisterNewsletter,
          t(`errors.${errorUnregisterNewsletter}`, t("errors.default"))
        );
        clearUnregisterNewsletter();
        recaptchaRef.current.reset();
      } else if (dataUnregisterNewsletter) {
        logDebug("NewsletterUnregisterDialog", "newsletterUnregister", {
          data: dataUnregisterNewsletter,
          msg: t(`message.${dataUnregisterNewsletter}`, t("message.default")),
        });
        successToast(
          "NewsletterUnregisterDialog - newsletterUnregister",
          dataUnregisterNewsletter,
          t(`message.${dataUnregisterNewsletter}`, t("message.default"))
        );
        reset(defaultValues);
        recaptchaRef.current.reset();
        setIsBlocked(false);
        setTimeout(() => {
          dispatch(setBlur(false));
          dispatch(setShowNewsletterUnregisterDialog(false));
        }, 100);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataUnregisterNewsletter,
    errorUnregisterNewsletter,
    loadingUnregisterNewsletter,
  ]);

  return (
    <>
      <div className="wrapper"></div>
      <div ref={dialogNewsletterRef} className="newsletter__dialog">
        <div className="newsletter__dialog__header">
          <span className="newsletter__dialog__header__logo"></span>
          <div className="newsletter__dialog__header__title header-3">
            {t("unreg_newsletter_dialog.title")}
          </div>
          <div className="newsletter__dialog__header__subtitle text-body">
            {t("unreg_newsletter_dialog.subtitle")}
          </div>
        </div>
        <div className="newsletter__dialog__body">
          <NewsletterUnregForm
            form={form}
            loading={loadingForm}
            onValid={(data, evt) => onValidHandler(data, evt)}
            onInvalid={(errors, evt) => onInvalidHandler(errors, evt)}
          />
        </div>
        <div className="newsletter__dialog__footer"></div>
      </div>
      <ReCAPTCHA
        ref={recaptchaRef}
        className="newsletter-reCAPTCHA"
        sitekey={process.env.REACT_APP_RECAPTCHA_KEY}
        badge="bottomright"
        size="invisible"
        onErrored={(evt) => onErroredReCaptchaHandler(evt)}
        onExpired={(evt) => onExpiredReCaptchaHandler(evt)}
        asyncScriptOnLoad={(evt) => asyncScriptOnLoadReCaptchaHandler(evt)}
      />
      <DevTool id="newsletter-unregister-dialog__devtools" control={control} />
    </>
  );
}

export default NewsletterUnregisterDialog;
